import { allowedOrigins, isAllowedOrigin } from './origins';

const SUPPORTED_VERSION = 1;

// Implements Bloomreach's Widget Webhook postMessage protocol; one bridge per page load.
export function createBridge({ onHello, onRequestState, onReject, onErrors, onStandalone } = {}) {
  let appOrigin = null;
  let rejected = false;

  const isEmbedded = () => window.parent !== window;

  const post = (message, targetOrigin) => {
    if (!isEmbedded() || !targetOrigin) return;
    window.parent.postMessage(message, targetOrigin);
  };

  const sendState = (state) => {
    if (!appOrigin) return;
    post({ message_type: 'widget_state', ...state }, appOrigin);
  };

  const handleMessage = (event) => {
    if (rejected) return;

    // Origin check: pre-pin must be a Bloomreach origin, post-pin must match the pinned origin.
    if (appOrigin ? event.origin !== appOrigin : !isAllowedOrigin(event.origin)) {
      return;
    }

    const data = event.data;
    if (!data || typeof data !== 'object' || !data.message_type) return;

    switch (data.message_type) {
      case 'app_hello':
        appOrigin = event.origin;
        // Answer synchronously before onHello — Bloomreach shows a spinner until this posts.
        post({ message_type: 'widget_initialized' }, appOrigin);
        onHello?.({ webhook: data.webhook ?? null, widget_state: data.widget_state ?? null });
        break;

      case 'app_reject':
        rejected = true;
        onReject?.();
        break;

      case 'app_request_state': {
        // app_request_state fires for Test/Done/Cancel alike (never a commit) — always answer.
        const state = onRequestState ? onRequestState() : null;
        sendState(state || { webhook: null, widget_state: null });
        break;
      }

      case 'errors':
        onErrors?.(data.errors);
        break;

      default:
        break;
    }
  };

  // Idempotent: StrictMode double-invokes effects in dev; only one listener/fanout wanted.
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    window.addEventListener('message', handleMessage);
    if (!isEmbedded()) {
      onStandalone?.();
      return;
    }
    const hello = {
      min_supported_version: SUPPORTED_VERSION,
      max_supported_version: SUPPORTED_VERSION,
      message_type: 'widget_hello',
    };
    // The parent's origin: ancestorOrigins (Chromium/Safari) survives a no-referrer policy;
    // document.referrer is the fallback. Unknown or untrusted parent: fan out to the exact list.
    const parentOrigin = (() => {
      const ancestor = window.location.ancestorOrigins?.[0];
      if (ancestor) return ancestor;
      if (!document.referrer) return null;
      try {
        return new URL(document.referrer).origin;
      } catch {
        return null;
      }
    })();
    if (parentOrigin && isAllowedOrigin(parentOrigin)) {
      post(hello, parentOrigin);
    } else {
      allowedOrigins().forEach((origin) => post(hello, origin));
    }
  };

  return { start, sendState, isEmbedded };
}
